import { Article, BreadcrumbList, ListItem, Product, WithContext } from "schema-dts";

import { homepageUrl } from "@/mk2/apps/home/urls";
import { getWikiArticleUrl } from "@/mk2/apps/wiki/urls";
import { isCategoryArticle, wikiArticleUrl, wikiCategoryUrl } from "@/mk2/apps/wiki/utils";
import { getFirstParagraphsPlaintext } from "@/mk2/helpers/article_ast/utils";
import {
    getCoverOrFirstPhoto,
    getPhotoResizedDimensions,
    getUrlOfCoverImageOrFirstImageOrFallback,
    organizationModrykonikWithoutContext,
} from "@/mk2/helpers/structuredData";
import { YMDHMSIso } from "@/mk2/helpers/timeformat";
import { fbMorykonikProfileUrl } from "@/mk2/helpers/urls";
import {
    ArticleConceptPath,
    WikiArticleEntity,
    WikiCategoryEntity,
    WikiEntityEntity,
} from "@/mk2/schemas";
import { photoUrl } from "@/mk/photo/photoUrl";
import { BASE_URL } from "@/mk/settings";

const breadcrumbCategory = (baseUrl: string, category: WikiCategoryEntity): ListItem => ({
    "@type": "ListItem",
    position: 1,
    item: {
        "@id": baseUrl + wikiCategoryUrl(category),
        name: category.name,
    },
});

const breadcrumbArticle = (baseUrl: string, article: WikiArticleEntity): ListItem => ({
    "@type": "ListItem",
    position: 2,
    item: {
        "@id": baseUrl + wikiArticleUrl(article),
        name: article.title,
    },
});

export function breadcrumbsWiki(baseUrl, article: WikiArticleEntity): WithContext<BreadcrumbList> {
    const items = [];

    items.push(breadcrumbCategory(baseUrl, article.category));

    if (!isCategoryArticle(article)) {
        items.push(breadcrumbArticle(baseUrl, article));
    }

    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items,
    };
}

export function conceptsBreadcrumbsWiki(
    baseUrl,
    articleConceptPath: ArticleConceptPath[],
): WithContext<BreadcrumbList> {
    const items: ListItem[] = articleConceptPath.map((concept, idx) => ({
        "@type": "ListItem",
        position: idx + 1,
        item: {
            "@id": baseUrl + getWikiArticleUrl(concept.categorySlug, concept.articleSlug),
            name: concept.title,
        },
    }));

    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items,
    };
}

export function reviewArticleWiki(baseUrl: string, reviewArticle: WikiArticleEntity) {
    const reviewArticleUrl = wikiArticleUrl(reviewArticle);

    // according to https://developers.google.com/search/docs/data-types/article#amp
    const result: WithContext<Product> = {
        "@context": "https://schema.org",
        "@type": "Product",
        "@id": baseUrl + reviewArticleUrl + "#Product",
        name: reviewArticle.title,
        image: getUrlOfCoverImageOrFirstImageOrFallback(
            reviewArticle.bodyAST,
            reviewArticle.photos,
        ),
        // Prefer the AI review summary (it summarizes the actual reviews and is
        // richer for AI/SERP); fall back to the article body. Only when published
        // (updatedAt set) — an admin-preview draft must not leak into JSON-LD.
        description:
            reviewArticle.aiReviewSummaryAST && reviewArticle.aiReviewSummaryUpdatedAt
                ? getFirstParagraphsPlaintext(reviewArticle.aiReviewSummaryAST)
                : getFirstParagraphsPlaintext(reviewArticle.bodyAST),
        sku: reviewArticle.id.toString(),
        url: baseUrl + reviewArticleUrl,
        ...(reviewArticle.gtin && { gtin: reviewArticle.gtin }),
    };
    const brandLink = reviewArticle.entityLinks?.find((link) => link.role === "brand");
    if (brandLink) {
        (result as any).brand = entityToSchemaOrg(brandLink.entity);
    }

    const linksWithRole = (role: string) =>
        (reviewArticle.entityLinks ?? [])
            .filter((l) => l.role === role)
            .map((l) => entityToSchemaOrg(l.entity));

    const authorLinks = linksWithRole("author");
    const reviewerLinks = linksWithRole("reviewer");
    const contributorLinks = linksWithRole("contributor");

    if (authorLinks.length) (result as any).author = authorLinks;
    if (contributorLinks.length) (result as any).contributor = contributorLinks;
    if (reviewerLinks.length) (result as any).reviewedBy = reviewerLinks;
    Object.assign(result, experienceReviewData(reviewArticle));
    return result;
}

// Builds the schema.org `review` array + `aggregateRating` from an article's user
// experiences (skusenosti). Both are valid on Product and on CreativeWork/Article,
// so it is shared by reviewArticleWiki() and articleWiki().
function experienceReviewData(article: WikiArticleEntity): {
    review?: object[];
    aggregateRating?: object;
} {
    // `experiences` is a normalized id→entity relation. On the review
    // list/homepage context the store hydrates only a curated subset of experience
    // entities (reviewCategoryArticlesExperiencesIds), so denormalizing the
    // article's full experiences id-list leaves the unhydrated refs as `undefined`
    // holes. Resolve to the hydrated entities before touching their fields.
    const experiences = (article.experiences ?? []).filter(Boolean);

    // Ratings only make sense when the article allows starred experiences.
    if (!article.experiencesStarsAllowed || !experiences.length) return {};

    const reviews = experiences
        // Only approved (moderated) starred experiences belong in JSON-LD. The FE
        // array also carries unapproved ones (shown with a "pending" badge to their
        // author), and aggregateRating below is approved-only — so filter to match.
        .filter((experience) => experience.approved && experience.starsCount > 0)
        .map((experience) => ({
            // No nested @context here: these Review objects are embedded inside the
            // parent Article/Product node. A nested @context makes Google's parser
            // treat each review as a separate root node, producing the
            // "Invalid object type for field '<parent_node>'" rich-results error.
            "@type": "Review",
            author: {
                "@type": "Person",
                // Guest (unregistered) reviews have no real account — use the nickname
                name: experience.authorNickname || experience.authorUser.username,
            },
            datePublished: experience.created,
            reviewBody: experience.text,
            reviewRating: {
                "@type": "Rating",
                bestRating: 5,
                worstRating: 1,
                ratingValue: experience.starsCount,
            },
        }));

    // Omit an empty review[] (e.g. the loaded experiences subset has no approved
    // starred ones) — aggregateRating from the denormalized totals can still stand alone.
    const result: { review?: object[]; aggregateRating?: object } = {};
    if (reviews.length) result.review = reviews;

    if (article.experiencesCount > 0 && (article.experiencesScore ?? false) !== false) {
        result.aggregateRating = {
            "@type": "AggregateRating",
            bestRating: 5,
            worstRating: 1,
            ratingValue: article.experiencesScore,
            reviewCount: article.experiencesCount,
            ratingCount: article.experiencesCount,
        };
    }

    return result;
}

const entityTypeToSchemaType: Record<string, string> = {
    brand: "Brand",
    organization: "Organization",
    medical_organization: "MedicalOrganization",
    medical_business: "MedicalBusiness",
    local_business: "LocalBusiness",
    service: "Service",
    person: "Person",
    medical_condition: "MedicalCondition",
    thing: "Thing",
    product: "Product",
};

// Subject entity types eligible to carry review + aggregateRating as a Google
// review-snippet node. MedicalCondition / Thing / Person / Brand / Service are NOT
// eligible for review rich results, so those subjects stay only as `about`.
const REVIEWABLE_SUBJECT_ENTITY_TYPES = new Set([
    "product",
    "organization",
    "medical_organization",
    "local_business",
    "medical_business",
]);

export function entityToSchemaOrg(entity: WikiEntityEntity) {
    const sameAs = [...(entity.sameAsUrls || [])];
    if (entity.wikidataQid) sameAs.push(`https://www.wikidata.org/wiki/${entity.wikidataQid}`);
    const isPerson = entity.entityType === "person";
    return {
        "@type": entityTypeToSchemaType[entity.entityType] ?? "Organization",
        name: entity.name,
        ...(entity.url && { url: entity.url }),
        ...(entity.photo && { image: photoUrl(entity.photo, "s150x150") }),
        ...(entity.description && { description: entity.description }),
        // Person reviewers carry their profession + institution (schema.org Person).
        ...(isPerson && entity.jobTitle && { jobTitle: entity.jobTitle }),
        ...(isPerson &&
            entity.affiliation && {
                affiliation: {
                    "@type": "Organization",
                    name: entity.affiliation,
                    ...(entity.affiliationUrl && { url: entity.affiliationUrl }),
                },
            }),
        ...(entity.entityType === "product" && entity.gtin && { gtin: entity.gtin }),
        ...(sameAs.length && { sameAs }),
    };
}

// True when the article has an approved-only star average worth exposing as
// aggregateRating (same condition that guards aggregateRating in experienceReviewData).
function articleHasStarRatings(article: WikiArticleEntity): boolean {
    return (
        !!article.experiencesStarsAllowed &&
        article.experiencesCount > 0 &&
        (article.experiencesScore ?? false) !== false
    );
}

// The subject entity that should carry the review + aggregateRating rich-results node,
// or null. Requires both a reviewable-typed `subject` link and actual star ratings.
function reviewableSubjectEntity(article: WikiArticleEntity): WikiEntityEntity | null {
    if (!articleHasStarRatings(article)) return null;
    const link = (article.entityLinks ?? []).find(
        (l) => l.role === "subject" && REVIEWABLE_SUBJECT_ENTITY_TYPES.has(l.entity?.entityType),
    );
    return link?.entity ?? null;
}

export function articleWiki(baseUrl: string, article: WikiArticleEntity): WithContext<Article> {
    const createdOn: string = YMDHMSIso(article.createdAt);
    const modifiedOn: string = YMDHMSIso(article.modifiedAt);
    const articleUrl = wikiArticleUrl(article);

    const linksWithRole = (role: string) =>
        (article.entityLinks ?? [])
            .filter((l) => l.role === role)
            .map((l) => entityToSchemaOrg(l.entity));

    const authorLinks = linksWithRole("author");
    const reviewerLinks = linksWithRole("reviewer");
    const contributorLinks = linksWithRole("contributor");
    const subjectLinks = linksWithRole("subject");

    // A reviewable subject is emitted as a standalone #subject node (carrying review +
    // aggregateRating) by reviewableSubjectForArticle(). Here we only reference it by @id
    // (from `about` / `mainEntity`) instead of duplicating the full entity object.
    const reviewableSubject = reviewableSubjectEntity(article);
    const subjectId = baseUrl + articleUrl + "#subject";
    let aboutValue: object | object[] | undefined;
    if (reviewableSubject) {
        aboutValue = { "@id": subjectId };
    } else if (subjectLinks.length === 1) {
        aboutValue = subjectLinks[0];
    } else if (subjectLinks.length) {
        aboutValue = subjectLinks;
    }

    // according to https://developers.google.com/search/docs/data-types/article#amp
    return {
        "@context": "https://schema.org",
        "@type": "Article",
        "@id": baseUrl + articleUrl + "#article",
        headline: article.title,
        description: getFirstParagraphsPlaintext(article.bodyAST),
        image: getUrlOfCoverImageOrFirstImageOrFallback(article.bodyAST, article.photos),
        datePublished: createdOn,
        dateModified: modifiedOn ? modifiedOn : createdOn,
        author: authorLinks.length ? authorLinks : organizationModrykonikWithoutContext(false),
        ...(contributorLinks.length && { contributor: contributorLinks }),
        // Anchor the article's topic to the knowledge graph (Wikidata sameAs) — a key
        // GEO/SEO signal. For a reviewable subject this is a bare @id reference to the
        // standalone #subject node; otherwise the full inline entity. "subject" is in
        // INLINE_ROLES so it is not also emitted as a standalone node by entitiesForArticle().
        ...(aboutValue && { about: aboutValue }),
        // Point the Article at its reviewable subject so Google can connect the two
        // root nodes on this URL (the #subject node carries review + aggregateRating).
        ...(reviewableSubject && { mainEntity: { "@id": subjectId } }),
        mainEntityOfPage: {
            "@type": "WebPage",
            "@id": baseUrl + articleUrl,
            ...(reviewerLinks.length && { reviewedBy: reviewerLinks }),
        },
        publisher: organizationModrykonikWithoutContext(false),
        // NOTE: review[] / aggregateRating are intentionally NOT on the Article node —
        // Google does not support review rich results for Article. For reviewable
        // subjects they live on the standalone #subject node instead; see
        // reviewableSubjectForArticle() / experienceReviewData().
    } as WithContext<Article>;
}

// Standalone review-snippet node for the article's reviewable subject (porodnica, liek,
// institution …). Emitted as a sibling root node next to the Article, carrying review +
// aggregateRating that Google does not support on Article. Returns [] when the article
// has no reviewable-typed `subject` entity or no star ratings. The Article node references
// this node by @id via `about` / `mainEntity`.
export function reviewableSubjectForArticle(baseUrl: string, article: WikiArticleEntity): object[] {
    const entity = reviewableSubjectEntity(article);
    if (!entity) return [];

    const articleUrl = wikiArticleUrl(article);
    const photo = article.coverPhoto || getCoverOrFirstPhoto(article.bodyAST, article.photos);

    return [
        {
            "@context": "https://schema.org",
            ...entityToSchemaOrg(entity),
            "@id": baseUrl + articleUrl + "#subject",
            url: entity.url || baseUrl + articleUrl,
            // Real cover/first photo only — no generic fallback placeholder as a subject image.
            ...(photo && { image: photoUrl(photo, "s1600x1600") }),
            ...experienceReviewData(article),
        },
    ];
}

// Roles embedded inline in the Article/WebPage block — skip as standalone entities to avoid duplication
const INLINE_ROLES = new Set(["author", "reviewer", "contributor", "subject"]);

export function entitiesForArticle(article: WikiArticleEntity): object[] {
    if (!article.entityLinks?.length) return [];

    return article.entityLinks
        .filter((link) => !INLINE_ROLES.has(link.role))
        .map((link) => ({
            "@context": "https://schema.org",
            ...entityToSchemaOrg(link.entity),
        }));
}

export function videoObjectsForArticle(article: WikiArticleEntity): object[] {
    if (!article.videoLinks?.length) return [];
    return article.videoLinks.map((link) => ({
        "@context": "https://schema.org",
        ...link.video.jsonld,
    }));
}

export function articleWikiOG(baseUrl: string, article: WikiArticleEntity) {
    const createdOn: string = YMDHMSIso(article.createdAt);
    const modifiedOn: string = YMDHMSIso(article.modifiedAt);
    const articleUrl = wikiArticleUrl(article);
    const mainPhoto = article.coverPhoto || getCoverOrFirstPhoto(article.bodyAST, article.photos);
    const dimensions = mainPhoto ? getPhotoResizedDimensions(mainPhoto, 1600) : null;

    // according to https://developers.facebook.com/docs/reference/opengraph/object-type/article/
    return {
        "og:type": "article",
        "og:title": article.title,
        "og:description": getFirstParagraphsPlaintext(article.bodyAST),
        "og:image": article.coverPhoto
            ? photoUrl(article.coverPhoto, "s1600x1600")
            : getUrlOfCoverImageOrFirstImageOrFallback(article.bodyAST, article.photos),
        "og:image:alt": article.title,
        ...(dimensions && {
            "og:image:width": String(dimensions.width),
            "og:image:height": String(dimensions.height),
        }),
        "og:url": baseUrl + articleUrl,

        "article:author": fbMorykonikProfileUrl(),
        "article:content_tier": "free",
        "article:published_time": createdOn,
        "article:modified_time": modifiedOn ? modifiedOn : createdOn,
        "article:publisher": BASE_URL + homepageUrl,
    };
}
